import { create } from 'zustand';

interface LoadingState {
  // Global loading state
  isLoading: boolean;
  loadingMessage: string | null;

  // Track individual loading operations by key
  loadingOperations: Map<string, { message?: string; startTime: number }>;

  // Actions
  startLoading: (key?: string, message?: string) => void;
  stopLoading: (key?: string) => void;
  setLoadingMessage: (message: string | null) => void;
  isOperationLoading: (key: string) => boolean;
  clearAllLoading: () => void;
}

export const useLoadingStore = create<LoadingState>((set, get) => ({
  isLoading: false,
  loadingMessage: null,
  loadingOperations: new Map(),

  startLoading: (key = 'global', message) => {
    set((state) => {
      const newOperations = new Map(state.loadingOperations);
      newOperations.set(key, { message, startTime: Date.now() });
      return {
        loadingOperations: newOperations,
        isLoading: true,
        loadingMessage: message || state.loadingMessage,
      };
    });
  },

  stopLoading: (key = 'global') => {
    set((state) => {
      const newOperations = new Map(state.loadingOperations);
      newOperations.delete(key);
      const hasActiveOperations = newOperations.size > 0;
      return {
        loadingOperations: newOperations,
        isLoading: hasActiveOperations,
        loadingMessage: hasActiveOperations ? state.loadingMessage : null,
      };
    });
  },

  setLoadingMessage: (message) => {
    set({ loadingMessage: message });
  },

  isOperationLoading: (key) => {
    return get().loadingOperations.has(key);
  },

  clearAllLoading: () => {
    set({
      isLoading: false,
      loadingMessage: null,
      loadingOperations: new Map(),
    });
  },
}));

// Hook for async operations with automatic loading state management
export function useLoadingOperation<T>(
  key: string
): [(operation: () => Promise<T>, message?: string) => Promise<T>, boolean] {
  const { startLoading, stopLoading, isOperationLoading } = useLoadingStore();
  const isLoading = isOperationLoading(key);

  const execute = async (operation: () => Promise<T>, message?: string): Promise<T> => {
    startLoading(key, message);
    try {
      return await operation();
    } finally {
      stopLoading(key);
    }
  };

  return [execute, isLoading];
}
